import Button, { ButtonType } from 'components/custom-button/CustomButton';
import { FC, useEffect, useState } from 'react';
import { AdjustmentSourceType, AdjustmentStatus, AdjustmentType } from '../constants';
import { PlusIconBlue } from 'assets/icons';
import AddAdjustmentModal, { CreateAdjustmentRequest } from './AddAdjustmentModal';
import OtherAdjustmentsTable from './OtherAdjustmentsTable';
import {
  useCreateAdjustmentMutation,
  useLazyGetSuggestedAdjustmentsForMemberQuery,
  useUpdateAdjustmentMutation,
  useUpdateAdjustmentStatusMutation
} from '../api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useParams } from 'react-router-dom';
import { Adjustment, AdjustmentFormData } from '../payrun.model';
import PayAdjustmentBanner from './PayAdjustmentBanner';
import ReviewAdjustmentSuggestionsModal from './ReviewAdjustmentSuggestionsModal';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../events';

interface MemberAdjustmentsTabProps {
  adjustments: Adjustment[];
  suggestedAdjustmentCount: number;
  totalAdjustmentAmount: string;
}

const MemberAdjustmentsTab: FC<MemberAdjustmentsTabProps> = ({
  adjustments = [],
  suggestedAdjustmentCount,
  totalAdjustmentAmount
}) => {
  const { merchantId, payRunId } = useParams();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { managePayAdjustments } = currentTeamAccountingPermissions ?? {};

  const [createAdjustment, { isLoading: isCreatingAdjustment }] = useCreateAdjustmentMutation();
  const [updateAdjustment, { isLoading: isUpdatingAdjustment }] = useUpdateAdjustmentMutation();
  const [updateAdjustmentStatus, { isLoading: isUpdatingAdjustmentStatus }] = useUpdateAdjustmentStatusMutation();
  const [getSuggestedAdjustmentsQuery, { isFetching: isFetchingSuggestedAdjustments, data: suggestedAdjustments }] =
    useLazyGetSuggestedAdjustmentsForMemberQuery();

  const [isAddAdjustmentModalOpen, setIsAddAdjustmentModalOpen] = useState({ isOpen: false, type: null });
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);

  const getSuggestedAdjustments = () => {
    getSuggestedAdjustmentsQuery({
      teamId: currentTeam.id,
      merchantId: merchantId as string,
      payRunId: payRunId as string
    });
  };
  useEffect(() => {
    if (currentTeam?.id) {
      getSuggestedAdjustments();
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentTabView, {
        payRunId,
        merchantId
      });
    }
  }, [currentTeam?.id]);

  const handleSaveAdjustment = async (adjustmentData: CreateAdjustmentRequest) => {
    try {
      await createAdjustment({
        title: adjustmentData.title,
        amount: adjustmentData.amount,
        postedDate: adjustmentData.date,
        notes: adjustmentData.description,
        receiptUrl: adjustmentData.receiptUrl,
        merchantId,
        teamId: currentTeam.id,
        type: AdjustmentSourceType.MERCHANT,
        payRunId: payRunId
      }).unwrap();
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentCreated, {
        payRunId,
        merchantId
      });
      setIsAddAdjustmentModalOpen({ isOpen: false, type: null });
    } catch (error) {
      // Error handling is done by the API's extraOptions
    }
  };

  const handleAddAdjustment = (type: AdjustmentType) => {
    setIsAddAdjustmentModalOpen({ isOpen: true, type });
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAddAdjustmentClick, {
      payRunId,
      merchantId
    });
  };

  const handleReviewClick = () => {
    setCurrentSuggestionIndex(0);
    setIsReviewModalOpen(true);
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentReviewClick, {
      payRunId,
      merchantId,
      source: 'pay-run-member-adjustments-tab'
    });
  };

  const handleDiscard = async (adjustmentId: string) => {
    await updateAdjustmentStatus({
      adjustmentId,
      status: AdjustmentStatus.REJECTED,
      teamId: currentTeam.id
    });
    handleNextSuggestion();
  };

  const handleDecideLater = async (adjustmentData: AdjustmentFormData, adjustmentId: string, isEdited: boolean) => {
    if (isEdited) {
      await updateAdjustment({
        ...adjustmentData,
        adjustmentId,
        merchantId,
        teamId: currentTeam.id,
        status: AdjustmentStatus.REMIND_ME_LATER
      });
    } else {
      await updateAdjustmentStatus({
        adjustmentId,
        status: AdjustmentStatus.REMIND_ME_LATER,
        teamId: currentTeam.id
      });
    }
    handleNextSuggestion();
  };

  const handleAcceptSuggestedAdjustment = async (
    adjustmentData: AdjustmentFormData,
    adjustmentId: string,
    isEdited: boolean
  ) => {
    if (isEdited) {
      await updateAdjustment({
        ...adjustmentData,
        adjustmentId,
        merchantId,
        teamId: currentTeam.id,
        status: AdjustmentStatus.ACCEPTED
      });
    } else {
      await updateAdjustmentStatus({
        adjustmentId,
        status: AdjustmentStatus.ACCEPTED,
        teamId: currentTeam.id
      });
    }
    handleNextSuggestion();
  };

  const handleNextSuggestion = async () => {
    const resp = await getSuggestedAdjustmentsQuery({
      teamId: currentTeam.id,
      merchantId: merchantId as string,
      payRunId: payRunId as string
    });
    const adjustmentsData = resp?.data?.adjustments;
    if (adjustmentsData?.length === 0 || !adjustmentsData) {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
      return;
    }
    if (suggestedAdjustments && currentSuggestionIndex < suggestedAdjustments.adjustments.length - 1) {
      setCurrentSuggestionIndex(prev => prev + 1);
    } else {
      setIsReviewModalOpen(false);
      setCurrentSuggestionIndex(0);
    }
  };

  return (
    <div className="mx-auto flex h-full w-4/5 max-w-5xl flex-col">
      {+suggestedAdjustmentCount > 0 && managePayAdjustments && (
        <div className="mb-6">
          <PayAdjustmentBanner
            suggestionsCount={suggestedAdjustmentCount}
            onReviewClick={handleReviewClick}
            isLoading={isFetchingSuggestedAdjustments}
          />
        </div>
      )}
      {adjustments && adjustments.length > 0 ? (
        <OtherAdjustmentsTable
          adjustments={adjustments}
          totalAdjustmentAmount={totalAdjustmentAmount}
          onAddEarning={() => handleAddAdjustment(AdjustmentType.EARNING)}
          onAddDeduction={() => handleAddAdjustment(AdjustmentType.DEDUCTION)}
          hasManagePayAdjustments={managePayAdjustments}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center py-8">
          <div className="flex flex-col items-center justify-center">
            <h2 className="mb-2 text-xl font-semibold text-heading">No adjustments added yet</h2>
            {managePayAdjustments && (
              <>
                <p className="mb-6 text-center text-17px font-semibold text-primaryText">
                  Start by adding an earning or deduction for the member
                </p>
                <div className="flex gap-4">
                  <Button
                    id="add-earning-btn"
                    type={ButtonType.SECONDARY}
                    onClick={() => handleAddAdjustment(AdjustmentType.EARNING)}
                    className="flex items-center gap-2 border border-borderGray"
                    StartIcon={PlusIconBlue}>
                    <div className="text-17px font-semibold">Add earning</div>
                  </Button>
                  <Button
                    id="add-deduction-btn"
                    type={ButtonType.SECONDARY}
                    onClick={() => handleAddAdjustment(AdjustmentType.DEDUCTION)}
                    className="flex items-center gap-2 border border-borderGray"
                    StartIcon={PlusIconBlue}>
                    <div className="text-17px font-semibold">Add deduction</div>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isAddAdjustmentModalOpen.isOpen && (
        <AddAdjustmentModal
          open={isAddAdjustmentModalOpen.isOpen}
          type={isAddAdjustmentModalOpen.type}
          onClose={() => setIsAddAdjustmentModalOpen({ isOpen: false, type: null })}
          onSave={handleSaveAdjustment}
          sourceType={AdjustmentSourceType.MERCHANT}
          assignees={[]}
          isLoading={isCreatingAdjustment || isUpdatingAdjustment}
        />
      )}
      {isReviewModalOpen && suggestedAdjustments && suggestedAdjustments.adjustments?.length > 0 && (
        <ReviewAdjustmentSuggestionsModal
          open={isReviewModalOpen}
          onClose={() => {
            setIsReviewModalOpen(false);
            setCurrentSuggestionIndex(0);
          }}
          suggestedAdjustment={suggestedAdjustments.adjustments[currentSuggestionIndex]}
          onDiscard={handleDiscard}
          onDecideLater={handleDecideLater}
          onAddAdjustment={handleAcceptSuggestedAdjustment}
          currentIndex={currentSuggestionIndex}
          totalSuggestions={suggestedAdjustments.adjustments.length}
          assignees={[]}
          sourceType={AdjustmentSourceType.MERCHANT}
          isLoading={isUpdatingAdjustment || isUpdatingAdjustmentStatus || isFetchingSuggestedAdjustments}
        />
      )}
    </div>
  );
};

export default MemberAdjustmentsTab;
